/**
 * 🔴 **THE SIDEBAR STOPS OFFERING DOORS THAT WILL NOT OPEN** (D54).
 *
 * Found by the persona picker (D53) the minute it existed: signing in as Rudi — a member — his rail
 * still offered **Admin**, a screen the gate refuses him. `navFilter.ts` filters by which modules the
 * TENANT has and never by what the PERSON holds, so every destination was offered to everybody and
 * the refusal happened after the click.
 *
 * 🔴 **This is honesty, not security, and the distinction decides how it fails.** The gate is the
 * guard and is untouched — six layers, re-read from the database on every request. Nothing here may
 * ever be the reason something is allowed; it only decides what is DRAWN. So the asymmetry is
 * deliberate:
 *
 *   · a route we can classify, whose scope the person lacks   → HIDE it
 *   · a route we cannot classify                              → SHOW it
 *   · scopes not known yet, or no organisation chosen         → SHOW everything
 *
 * Hiding on uncertainty is the worse error by far. A door that opens onto a refusal is a small
 * insult; a person who cannot find a screen they are entitled to has no way to discover it exists,
 * and will be told the product is broken. Failing open here is safe precisely BECAUSE the gate does
 * not.
 */
import {
  ACCESS_MODULES,
  isAccessScope,
  isPlatformScope,
  scopeOf,
  type AccessModule,
} from "@pinksy/contracts";
import type { NavBarItem } from "@/app/types";

/**
 * 🔴 **THE ROUTE ROOT IS NOT THE SCOPE, AND ASSUMING IT WAS LEFT FIVE RAIL ROWS UNGATED.**
 *
 * This used to be `ACCESS_MODULES.includes(firstSegment)`, on the strength of a comment that read
 * *"the tenant's tile routes and `ACCESS_MODULES` line up exactly today (all nine)"*. They stopped
 * lining up almost immediately, and nothing said so — an unclassifiable root fails OPEN by design
 * (below), so the drift showed up as rows that were simply never hidden from anybody:
 *
 *   · `/contacts` — migration 037 moved `@pinksy/organisations` off `/organisations`. The scope
 *     `organisations:read` still exists and `GET /contacts` still requires it; only the identity
 *     between the route root and the module's name broke, so the row stopped being gated.
 *   · `/analytics`, `/artists` — modules cut after this file was written, with no scope of their own
 *     (`MODULES_WITHOUT_A_SCOPE` in the contracts says why for each).
 *   · `/management`, `/profile` — the same, and correctly so; they are listed here anyway, because a
 *     deliberate blank and a forgotten one look identical when both are spelled "absent".
 *
 * So the map is written out. What a row needs is **the weakest scope its own first read requires** —
 * taken from `services/auth/endpoint-scopes.ts`, which is the register the gate actually uses, and
 * never guessed from the word in the URL.
 *
 * 🔴 **The Desk's sitemap lives in the shell** (D115: a shared package holding the product's sitemap
 * is not a design system), so this table is here and not in `@pinksy/contracts`. Its counterpart
 * there — `MODULES_WITHOUT_A_SCOPE` / `SCOPES_WITHOUT_A_MODULE` — records which names are authorities
 * and which are places; this records which door needs which key.
 */
const ROUTE_SCOPES: Readonly<Record<string, AccessModule | null>> = {
  // `@pinksy/organisations`, mounted at `/contacts` since migration 037. `GET /contacts` →
  // `organisations:read`, and every built-in role holds it, so nobody who can open it loses the row.
  contacts: "organisations",
  // Dashboards. Every `/analytics/…` endpoint takes `organisations:read` on purpose — the register is
  // emphatic that an `analytics:read` must never exist, because it would be a door round every other
  // scope in the file. Each DATASET is re-gated on its own scope inside the handler, and the
  // catalogue is filtered by the same predicate, so a person is never offered a widget that would 403.
  analytics: "organisations",
  // Artist Management (D92). Its landing read is `GET /organisations/:id/people` →
  // `organisations:read`; the roster writes are the `/talent` pair at `people:write`.
  artists: "organisations",
  // 🔴 DELIBERATELY UNGATED — migration 030 (D90): *"Every Company should have a managment section…
  // there is no scope carved for it"*. Its thirteen panes are gated server-side on thirteen different
  // scopes, which is exactly why the place itself carries none.
  management: null,
  // 🔴 DELIBERATELY UNGATED — a person's OWN record (D67). Every endpoint behind it is `/me/…` and
  // PUBLIC by decision: the register says an organisation scope here *"would make it belong to
  // whichever company happened to be selected in the sidebar"*, and a person who has joined no company
  // still has a profile.
  profile: null,
  // Payments (PNK-246). A place with no authority of its own yet: it is money a SHOW spends, so its
  // doors are `costs` ones until Devin decides otherwise (PNK-245) — the rows' own standing narrows them
  // further to `cost.detail` / `cost.approvals` on the production.
  payments: "costs",
};

/**
 * The scope module a route answers to — `/costs?section=topsheet` → `costs`, `/contacts` →
 * `organisations`. `ROUTE_SCOPES` first, then the identity for the roots that ARE scope modules; a
 * root this file has never heard of returns null and its row is left alone.
 */
export function moduleOfRoute(route: string): AccessModule | null {
  const root =
    (route.split("?")[0] ?? route).split("/").filter(Boolean)[0] ?? "";
  // 🔴 `Object.hasOwn`, never `root in ROUTE_SCOPES`: `in` walks the prototype, so a route
  // `/constructor` or `/toString` would "find" an entry and hand back a function as a module name.
  if (Object.hasOwn(ROUTE_SCOPES, root)) return ROUTE_SCOPES[root] ?? null;
  return (ACCESS_MODULES as readonly string[]).includes(root)
    ? (root as AccessModule)
    : null;
}

/** What a person needs to be offered this route at all: the weakest thing that opens the screen. */
export function scopeForRoute(route: string): string | null {
  const module = moduleOfRoute(route);
  return module === null ? null : scopeOf(module, "read");
}

export interface HeldScopes {
  /** `<module>:<action>`, already expanded by the gate (`read ⊂ write ⊂ admin`). */
  access: ReadonlyArray<string>;
  /** `admin` / `system-admin` from `tagId` (D42) — opens the platform module and nothing else (D50). */
  platform: ReadonlyArray<string>;
  /**
   * 🔴 False until the server has answered. While false NOTHING is hidden — a sidebar that empties
   * itself for a moment on every boot, and refills, is worse than one that briefly offers too much.
   */
  known: boolean;
}

/** Whether these scopes open that route. Mirrors `services/auth/scope-middleware.ts:decide`, narrowed to reads. */
export function opensRoute(route: string, held: HeldScopes): boolean {
  if (!held.known) return true;
  const needed = scopeForRoute(route);
  if (needed === null || !isAccessScope(needed)) return true;
  // D50: platform authority opens the platform module, and only that.
  if (isPlatformScope(needed)) return held.platform.length > 0;
  return held.access.includes(needed);
}

/**
 * The entries this person may actually reach. A parent keeps only the children they may reach, and a
 * parent left with none of its own sections — having had some — goes with them: a context you enter
 * onto an empty rail is a dead end (D51).
 */
export function filterNavByScopes(
  items: NavBarItem[],
  held: HeldScopes,
): NavBarItem[] {
  if (!held.known) return items;
  const out: NavBarItem[] = [];
  for (const item of items) {
    if (!opensRoute(item.route, held)) continue;
    if (item.items && item.items.length > 0) {
      const children = item.items.filter((sub) => opensRoute(sub.route, held));
      if (children.length === 0) continue;
      out.push({ ...item, items: children });
      continue;
    }
    out.push(item);
  }
  return out;
}

/**
 * 🔴 **IN NO COMPANY IS AN ANSWER, NOT UNCERTAINTY** (D157, found in Chrome as Pieter).
 *
 * D54 fails open while the scopes are unknown — and with no company chosen they are never asked for,
 * so somebody who has joined no company at all was offered every company door: Productions opened onto
 * *"needs 'productions:read' … (HTTP 403)"*, and Management, Artist Management and Admin sat beside it.
 * Once `GET /me/organisations` has ANSWERED with nothing selectable, "holds no company scope" is a
 * fact. So the rail keeps only what is theirs: their own record and the shows they are booked on.
 * Management is a company's own place (D90) and goes too, although it carries no scope.
 */
export const NO_COMPANY_SCOPES: HeldScopes = {
  access: [],
  platform: [],
  known: true,
};

const COMPANY_PLACES: ReadonlyArray<string> = ["management"];

const isCompanyPlace = (route: string): boolean => {
  const root = (route.split("?")[0] ?? route).split("/").filter(Boolean)[0] ?? "";
  return COMPANY_PLACES.includes(root);
};

export function filterNavForNoCompany(items: NavBarItem[]): NavBarItem[] {
  return filterNavByScopes(items, NO_COMPANY_SCOPES).filter((item) => !isCompanyPlace(item.route));
}

/**
 * The same no-company decision for ONE route — the home grid's tiles (D164). A represented actor with any
 * number of agencies and no company must not be offered Management, Admin or Artist management on `/` any
 * more than on the rail.
 */
export function opensWithNoCompany(route: string): boolean {
  return opensRoute(route, NO_COMPANY_SCOPES) && !isCompanyPlace(route);
}
